/**
 * Offline outbox for driver mutations.
 *
 * Delivery vans lose signal. Without this, a completion attempted in a dead
 * zone simply failed and the driver had to stand there retrying; breadcrumbs
 * gathered while offline were dropped entirely.
 *
 * Operations are queued in IndexedDB (survives reload and tab crash, unlike
 * in-memory state) and replayed in order when connectivity returns. The server
 * treats a completion or failure of an already-resolved stop as success, so a
 * replayed request cannot double-apply or re-run the proximity gate against a
 * driver who has since moved on.
 *
 * Scope: this covers "app open, network down", which is the delivery-van case.
 * It is not a service worker — operations do not replay while the app is fully
 * closed. That would need Background Sync and a registered SW.
 */

const DB_NAME = "mediintel-driver"
const DB_VERSION = 1
const STORE = "outbox"

/**
 * Fired after queued operations actually reach the server.
 *
 * The drainer runs at the route boundary, so the screens showing delivery state
 * have no idea a replay just changed it. Rather than have them poll faster on
 * the off chance, they listen for this and reload once, when there is something
 * new to load.
 */
export const OUTBOX_SYNCED_EVENT = "driver:outbox-synced"

// Give up on an operation that has failed this many times, rather than
// retrying a permanently-rejected request forever.
const MAX_ATTEMPTS = 8

let dbPromise = null

function openDb() {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available"))
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true })
        store.createIndex("createdAt", "createdAt")
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  }).catch((err) => {
    // Don't cache a failed open — a later call may succeed.
    dbPromise = null
    throw err
  })

  return dbPromise
}

function tx(db, mode, run) {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, mode)
    const store = transaction.objectStore(STORE)
    let result

    try {
      result = run(store)
    } catch (err) {
      reject(err)
      return
    }

    transaction.oncomplete = () => resolve(result)
    transaction.onerror = () => reject(transaction.error)
    transaction.onabort = () => reject(transaction.error)
  })
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Queue an operation for delivery.
 *
 * @param {object} op
 * @param {"POST"} op.method
 * @param {string} op.url        API path
 * @param {object} op.body       JSON body
 * @param {string} op.kind       coarse label used for de-duplication and UI
 * @param {string} [op.dedupeKey] when set, an existing queued op with the same
 *                                key is replaced rather than appended
 */
export async function enqueue(op) {
  const db = await openDb()

  const record = {
    method: op.method || "POST",
    url: op.url,
    body: op.body ?? null,
    kind: op.kind || "unknown",
    dedupeKey: op.dedupeKey || null,
    attempts: 0,
    createdAt: Date.now(),
  }

  return tx(db, "readwrite", (store) => {
    if (!record.dedupeKey) {
      store.add(record)
      return
    }

    // Replace a superseded op (e.g. a second completion attempt for the same
    // stop) instead of queueing both.
    //
    // The add MUST wait until the cursor has finished. Adding up front puts the
    // new record at the highest key, so the ascending cursor reaches it last,
    // matches its own dedupeKey against it, and deletes the very record being
    // queued — the operation would vanish before anything could send it.
    const cursorRequest = store.openCursor()
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result

      if (!cursor) {
        // Iteration complete; every superseded entry is gone and it is now safe
        // to append.
        store.add(record)
        return
      }

      if (cursor.value?.dedupeKey === record.dedupeKey) {
        cursor.delete()
      }
      cursor.continue()
    }
  })
}

export async function pendingCount() {
  try {
    const db = await openDb()
    return await tx(db, "readonly", (store) => requestToPromise(store.count()))
  } catch {
    // A browser with IndexedDB unavailable simply has nothing queued.
    return 0
  }
}

/** All queued operations, oldest first. */
async function readAll() {
  const db = await openDb()
  return tx(db, "readonly", (store) => requestToPromise(store.getAll()))
}

async function remove(id) {
  const db = await openDb()
  return tx(db, "readwrite", (store) => store.delete(id))
}

async function update(record) {
  const db = await openDb()
  return tx(db, "readwrite", (store) => store.put(record))
}

/**
 * Replay queued operations in order.
 *
 * Stops at the first network failure so ordering is preserved — a completion
 * queued after a breadcrumb batch must not overtake it. A 4xx means the server
 * rejected the operation outright; retrying won't help, so it is dropped and
 * reported rather than blocking the queue forever.
 *
 * @param {(op) => Promise<void>} send performs the request
 * @returns {Promise<{sent: number, dropped: Array, remaining: number}>}
 */
// Guards against overlapping drains. The outbox is woken by the online event,
// visibility changes and a timer, so without this two flushes can read the same
// records and send them both.
let flushInFlight = null

export async function flush(send) {
  // Join the run already in progress rather than racing it.
  if (flushInFlight) return flushInFlight

  flushInFlight = runFlush(send).finally(() => {
    flushInFlight = null
  })

  return flushInFlight
}

async function runFlush(send) {
  let records
  try {
    records = await readAll()
  } catch (err) {
    console.error("[outbox] read failed:", err)
    return { sent: 0, dropped: [], remaining: 0 }
  }

  records.sort((a, b) => a.createdAt - b.createdAt || a.id - b.id)

  let sent = 0
  const dropped = []

  for (const record of records) {
    try {
      await send(record)
      await remove(record.id)
      sent += 1
    } catch (err) {
      const status = err?.status ?? err?.response?.status ?? null

      // An expired session is not a verdict on the operation. Keep it queued so
      // it can go out after the driver logs back in — discarding a completed
      // delivery because a token lapsed would lose real work.
      if (status === 401) {
        break
      }

      // Any other answered 4xx means the server refused this operation. Sending
      // it again would be refused identically, so drop it and report why.
      const isClientRejection = status != null && status >= 400 && status < 500

      if (isClientRejection) {
        await remove(record.id)
        dropped.push({ record, reason: err?.message || `HTTP ${status}` })
        continue
      }

      const attempts = (record.attempts || 0) + 1
      if (attempts >= MAX_ATTEMPTS) {
        await remove(record.id)
        dropped.push({ record, reason: `Gave up after ${attempts} attempts` })
        continue
      }

      await update({ ...record, attempts })

      // Network-level failure: still offline. Preserve order and stop here.
      break
    }
  }

  let remaining = 0
  try {
    remaining = (await readAll()).length
  } catch {
    remaining = 0
  }

  return { sent, dropped, remaining }
}

export async function clear() {
  const db = await openDb()
  return tx(db, "readwrite", (store) => store.clear())
}

export function isOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine !== false
}
